import { FeatureHelperService } from './../_helpers/feature-helper/feature-helper.service';
import { ThemeService } from './../_helpers/theming-engine/theme.service';
import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '../_services/index';
import { AdministrationService } from '../_services/administration.service';
import { MsalService } from '@azure/msal-angular';
import { SessionUserHashManagerService } from '../_services/session-user-hash-manager/session-user-hash-manager.service';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { AuthenticationStateHelperService } from '../_helpers/authentication-state-helper/authentication-state-helper.service';
import { SuccessModalHelperService } from '../_helpers/success-modal-helper/success-modal-helper.service';

@Component({
    templateUrl: 'login.component.html',
    styleUrls: ['login.component.css'],
    standalone: false
})

export class LoginComponent implements OnInit {

  /** A second refusal within this long of being sent home means home refuses them too. */
  private static readonly refusedAgainWindowMs = 10000;

  model: any = {};
  user: any = {};
  returnUrl: string;
  showMfaTextField: boolean = false;
  public mfaMobileLastDigits: string = "1334";
  public companyType: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private msalService: MsalService,
    private authenticationService: AuthenticationService,
    private successModalHelper: SuccessModalHelperService,
    private administration: AdministrationService,
    public themeService: ThemeService,
    private featureHelperService: FeatureHelperService,
    private sessionHashService: SessionUserHashManagerService,
    private errorModalHelper: ErrorModalHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService
    ) { }

  async ngOnInit() {
    // A page this user may not open sends them here while they are still signed in. Send them home and say
    // why, rather than signing them out. If they are refused again straight after (home itself refuses
    // them), there is nowhere to send them, so they are signed out as before.
    if (this.authenticationStateHelper.unauthorizedToken && localStorage.getItem('currentUser')) {
      const now = Date.now();
      if (!(now - this.authenticationStateHelper.sentHomeAfterRefusalAt < LoginComponent.refusedAgainWindowMs)) {
        this.authenticationStateHelper.unauthorizedToken = false;
        this.authenticationStateHelper.sentHomeAfterRefusalAt = now;
        this.router.navigate(['/']);
        this.errorModalHelper.show('Your account does not have access to that page.', {}, 'No access');
        return;
      }
    }

    // reset login status
    if(this.msalService.instance.getAllAccounts().length > 0) {
      // Local sign-out only. logoutRedirect() navigates to Entra's end-session endpoint, which ends
      // the whole Entra session — signing the user out of Outlook, Teams, SharePoint and every other
      // app in that browser, not just LEAP. clearCache() drops MSAL's own cached account and tokens
      // without contacting Entra, so LEAP's session ends and nothing else is touched.
      // Note this runs on arriving at /login, not only on pressing log out: both logout buttons
      // simply route here, as do session timeouts and the unauthorised-response interceptor.
      //
      // Awaited so the cache is genuinely empty before anything else runs. Unawaited, getAllAccounts()
      // keeps reporting the signed-out account for a tick — harmless while ssoDefaultEntry is false
      // and nothing auto-challenges, but it would send a just-logged-out user straight back into a
      // sign-in the moment that flag is turned on. Everything below stays in its original order, so
      // the theme and return-URL setup are unaffected.
      try {
        await this.msalService.instance.clearCache();
      } catch (e) {
        // Never let a failed cache clear stop the login page initialising. Awaiting made this fatal
        // in a way the earlier fire-and-forget call was not: a rejection here skips everything below
        // — theme, return URL, the 401 modal — and the user gets a broken page instead of a login
        // form. Worst case the local MSAL cache survives, which is recoverable; a login page that
        // does not render is not.
        console.error('MSAL local sign-out failed; continuing to the login page.', e);
      }
    }

    this.authenticationService.logout();

    this.sessionHashService.clearSessionHash();
    
    // get return url from route parameters or default to '/'.
    // A page the user may not open sends them here with its own address as returnUrl. Returning them
    // to it after sign-in refuses them again, and they loop between that page and this one. So a
    // refused user signs back in to home, and the SSO redirect remembered for that page is dropped.
    if (this.authenticationStateHelper.unauthorizedToken) {
      sessionStorage.removeItem('login-redirect');
      this.authenticationStateHelper.returnUrl = this.returnUrl = '/';
    } else {
      this.authenticationStateHelper.returnUrl = this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    }

    if (this.authenticationStateHelper.unauthorizedToken) {
      this.errorModalHelper.show("", {status: 401});
    }

    this.companyType = 'LEAP';
    this.themeService.setActiveTheme('LEAP', false);
  }

  // Ticked when the user wants to sign in as someone other than the currently cached account —
  // testing another identity. Drives the prompt=select_account query param below.
  public challengeAccount: boolean = false;

  // The "Supporting Education Group Access" (Entra) button. Normally silent — go to /signmein and let
  // it challenge Entra, reusing the current SSO session. When challengeAccount is ticked, pass
  // prompt=select_account so /signmein forces the Microsoft account picker instead.
  public segAccessLogin(): void {
    this.router.navigate(['/signmein'], {
      queryParams: {
        returnUrl: this.returnUrl,
        prompt: this.challengeAccount ? 'select_account' : null
      }
    });
  }

  login() {
    
    this.authenticationService.login(this.model.username, this.model.password, this.companyType).subscribe((data: any) => {
        this.user = data;
        if (this.user.tutorIds && this.user.tutorIds[0] == null) {
          this.user.tutorIds = [];
        }

        if(data.mobile != null && data.mobile != "")
        {
            this.mfaMobileLastDigits = data.mobile.substr(-4,4);         
        }
        this.featureHelperService.setUserFeatures(data.userFeatures);
        this.setMfaFlag(data.mfaEnabled);

    },
    error => {
      if (error.status === 0) {
        this.errorModalHelper.show("Internal system error, please contact the system administrator", error);
      } else {
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  setMfaFlag(flag) {
    if (!flag) {
      this.setRouting();
    }
    else {
      this.generateMfa('activation');
    }
    this.showMfaTextField = flag;
  }

  generateMfa(type) {
    this.administration.createMfaCode(this.user.id).subscribe(result => {
      this.showMfaTextField = result > 0;
      if (type === 'resend') {
        this.successModalHelper.show("New authentication code has been sent");
      }
    }, error => {
      this.errorModalHelper.show("Error generating authentication code", error);
    });
  }

  public validateMfa() {
    var data = {
      userId: this.user.id,
      mfaCode: this.model.mfaCode
    };

    this.administration.validateMfaCode(data).subscribe(result => {
      this.setRouting();
    }, error => {
      this.errorModalHelper.show(error.message, error);
    })
  }

  setRouting() {
    this.authenticationService.setUser(this.user);
     
    setTimeout(() => {
      this.authenticationStateHelper.unauthorizedToken = false;
      this.router.navigateByUrl(this.returnUrl);
    }, 100);
    
  }

}
